import axios, { AxiosInstance, AxiosError } from 'axios';

/**
 * Zoku 앱 API 클라이언트
 *
 * 원본: .references/gc-mono/apps/gc-zoku/lib/web_api.js
 * baseURL: /api/ (단일 엔드포인트)
 * 인증: withCredentials (세션 쿠키 기반)
 */

interface ApiError {
  message: string;
  code?: number;
  isProcessed?: boolean;
}

type ErrorDispatcher = (error: ApiError) => void;

let errorDispatcher: ErrorDispatcher = (error) => {
  console.error('[webApi]', error.message);
};

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
  ? `${import.meta.env.VITE_API_BASE_URL}/api/`
  : '/api/';

const client: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 120 * 1000,
  withCredentials: true,
  responseType: 'json',
});

function processResponse<T>(response: { data: T }): T {
  return response.data;
}

function processError(err: AxiosError): Promise<never> {
  const response = err.response;
  let error: ApiError;

  if (response) {
    const data = response.data as { error?: ApiError } | undefined;
    error = data?.error ?? {
      message: err.message,
      code: response.status,
    };

    // 401 Unauthorized → 로그인 페이지로 리다이렉트
    if (response.status === 401) {
      window.location.href = '/login';
      return Promise.reject(error);
    }
  } else {
    error = { message: err.toString() };
  }

  errorDispatcher(error);
  error.isProcessed = true;
  return Promise.reject(error);
}

export const webApi = {
  post<T = unknown>(resource: string, data: Record<string, unknown> = {}): Promise<T> {
    return client.post<T>(resource, data)
      .then(processResponse)
      .catch(processError);
  },

  get<T = unknown>(resource: string, params?: Record<string, unknown>): Promise<T> {
    return client.get<T>(resource, { params })
      .then(processResponse)
      .catch(processError);
  },

  setErrorDispatcher(dispatcher: ErrorDispatcher): void {
    errorDispatcher = dispatcher;
  },

  getClient(): AxiosInstance {
    return client;
  },
};

export default webApi;
